import { useCallback, useEffect, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';

import { useStore, useLoader } from 'common/hooks';
import { CompanyMaster } from 'models/company/Company';
import {
    getCompanyPrivacyConfigForManage,
    saveCompanyPrivacyConfig
} from 'data/company/repositories/companyPrivacyConfig.repository';
import { CompanyPrivacyConfig } from 'data/company/schemas';

export type PrivacyConfigForm = {
    script_banner: string;
    cookie_notice_th: string;
    cookie_notice_en: string;
    privacy_notice_th: string;
    privacy_notice_en: string;
    is_active: boolean;
};

const EMPTY_FORM: PrivacyConfigForm = {
    script_banner: '',
    cookie_notice_th: '',
    cookie_notice_en: '',
    privacy_notice_th: '',
    privacy_notice_en: '',
    is_active: true
};

const ACTIVE_VALUES = ['true', 'y', 'yes', '1'];

function toForm(config?: CompanyPrivacyConfig): PrivacyConfigForm {
    if (!config) {
        return EMPTY_FORM;
    }

    return {
        script_banner: config.script_banner?.trim() ?? '',
        cookie_notice_th: config.cookie_notice_th?.trim() ?? '',
        cookie_notice_en: config.cookie_notice_en?.trim() ?? '',
        privacy_notice_th: config.privacy_notice_th?.trim() ?? '',
        privacy_notice_en: config.privacy_notice_en?.trim() ?? '',
        is_active: ACTIVE_VALUES.includes((config.is_active ?? '').trim().toLowerCase())
    };
}

function PrivacyConfigViewModel() {
    const loader = useLoader();
    const queryClient = useQueryClient();

    const { userCompanyStore, companyMasterStore } = useStore();
    const { companyMaster } = companyMasterStore;
    const { userRole, currentAdminCompany } = userCompanyStore;

    // Super admin can configure every company, admin only the companies they are admin of
    const companyOptions = useMemo(() => {
        switch (userRole) {
            case 'Super Admin':
                return companyMaster;
            case 'Admin':
                const sidSet = new Set(
                    currentAdminCompany
                        .filter(company => company.type === 'admin')
                        .map(company => company.sid)
                );
                return companyMaster.filter(company => sidSet.has(company.sid));
            default:
                return [];
        }
    }, [userRole, companyMaster, currentAdminCompany]);

    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();
    const [form, setForm] = useState<PrivacyConfigForm>(EMPTY_FORM);
    const [existingConfig, setExistingConfig] = useState<CompanyPrivacyConfig>();

    const fireSwal = useCallback(
        (title: string, message: string, icon: Parameters<typeof Swal.fire>[2]) => {
            return Swal.fire({
                title,
                text: message,
                icon,
                showCancelButton: false,
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Ok',
                allowOutsideClick: false
            });
        },
        []
    );

    // Initial fetches
    useEffect(() => {
        if (!userRole) {
            return;
        }

        companyMasterStore.fetchCompanyMasterStore('', 'active');
    }, [userRole, companyMasterStore]);

    const fetchConfig = useCallback(
        async (sid: string) => {
            try {
                loader.show();

                const [error, config] = await getCompanyPrivacyConfigForManage(sid);
                if (error) {
                    throw error;
                }

                setExistingConfig(config);
                setForm(toForm(config));
            } catch (error) {
                setExistingConfig(undefined);
                setForm(EMPTY_FORM);
                fireSwal('Error!', (error as Error).message, 'error');
            } finally {
                loader.hide();
            }
        },
        [loader, fireSwal]
    );

    const handleSelectCompany = useCallback(
        (company: SingleValue<CompanyMaster>) => {
            if (!company || !company.sid) {
                return;
            }

            setSelectedCompany(company);
            fetchConfig(company.sid);
        },
        [fetchConfig]
    );

    // Auto select and lock the company when the user can configure only one
    useEffect(() => {
        if (companyOptions.length === 1 && selectedCompany?.sid !== companyOptions[0].sid) {
            handleSelectCompany(companyOptions[0]);
        }
    }, [companyOptions]);

    const handleChange = <K extends keyof PrivacyConfigForm>(
        key: K,
        value: PrivacyConfigForm[K]
    ) => {
        setForm(prevState => ({ ...prevState, [key]: value }));
    };

    const handleReset = () => {
        setForm(toForm(existingConfig));
    };

    const handleSave = async () => {
        if (!selectedCompany) {
            return;
        }

        try {
            loader.show();

            const [error] = await saveCompanyPrivacyConfig({
                sid: selectedCompany.sid,
                ...form
            });
            if (error) {
                throw error;
            }

            // Refresh banner/footer for everyone on this browser
            queryClient.invalidateQueries({ queryKey: ['company-privacy-config'] });

            loader.hide();
            await fireSwal('Success', 'Privacy config has been saved.', 'success');
            fetchConfig(selectedCompany.sid);
        } catch (error) {
            loader.hide();
            fireSwal('Error!', (error as Error).message, 'error');
        }
    };

    return {
        userRole,
        companyOptions,
        selectedCompany,
        isCompanyLocked: companyOptions.length <= 1,
        isNewConfig: !!selectedCompany && !existingConfig,
        existingConfig,
        form,
        handleSelectCompany,
        handleChange,
        handleReset,
        handleSave
    };
}

export default PrivacyConfigViewModel;
